import { useRef, useState } from "react";

import { Link } from "@tanstack/react-router";

import { Banner, Button } from "@/components/ui";

import { MarketingShell } from "./MarketingShell";

/** A whole activity, at the shape the validator accepts. Kept beside `bundles/choice_board`. */
const MANIFEST = `schema_version = 1

[activity]
id = "quick_poll"
name = "Quick Poll"
version = "1.0.0"
sdk = "^0.1.0"
description = "Anyone watching picks one of a declared set."
lifecycle = "episodic"

[entities.option]
scope = "activity"
attributes = { picks = { min = 0.0, max = 1000000.0, default = 0.0 } }
tags = ["leading"]

[submissions.pick]
type = "input.choice.v1"
sender = "participant"
intake = "one"
once_per_sender = { from = "one_pick_each" }

[visibility]
"option.picks" = { visible = ["agent", "participant"] }

[canvases.participant]
scene = "canvases/participant.json"
attach = "owner"
scope = "participant"
required = true

[canvases.agent]
scene = "canvases/agent.json"
attach = "paired"
scope = "singleton"
required = true

[[settings]]
key = "options"
type = "string"
label = "Options, comma separated"
default = "a,b,c"

[[settings]]
key = "one_pick_each"
type = "bool"
label = "One pick each"
default = true
`;

const NEVER_GRANTED = [
  ["The machine's filesystem", "An output slot carries a value out of the activity."],
  ["Arbitrary outbound network calls", "The webhook effector, declared in the manifest."],
  ["Raw gaze and raw head pose", "A dwell event on a grid the activity declares."],
  ["Another activity's state", "A binding the customer wires between the two."],
] as const;

function Manifest() {
  const [said, setSaid] = useState("");
  const region = useRef<HTMLPreElement>(null);

  const copy = () => {
    void navigator.clipboard?.writeText(MANIFEST)?.catch(() => undefined);
    setSaid("Manifest copied");
  };

  return (
    <>
      <div className="mk-code-bar">
        <Button size="sm" variant="secondary" onClick={copy}>
          Copy manifest
        </Button>
        <span className="mk-said" role="status" aria-live="polite">
          {said}
        </span>
      </div>
      <pre className="mk-code" tabIndex={0} role="region" aria-label="Example manifest" ref={region}>
        {MANIFEST}
      </pre>
    </>
  );
}

/** Build an activity, per `docs/ui/marketing/publisher-signup.md`. The artifact comes first. */
export function Publish() {
  return (
    <MarketingShell>
      <section className="mk-page">
        <p className="mk-eyebrow">Build an activity</p>
        <h1 className="mk-h1">An activity is one manifest and a handler file</h1>
        <p className="mk-lede">
          Declare what you take from people, what you draw, and what you publish. The platform
          carries it out and supplies none of the meaning.
        </p>
      </section>

      <section className="mk-section">
        <h2 className="mk-h2">A whole activity</h2>
        <Manifest />
      </section>

      <section className="mk-section">
        <h2 className="mk-h2">Run it with no hardware</h2>
        <p className="mk-body">
          The test harness replays scripted events against your handlers. No eye tracker, no
          desktop app, no live stream and no account.
        </p>
      </section>

      <section className="mk-section">
        <h2 className="mk-h2">What is never granted</h2>
        <ul className="mk-never">
          {NEVER_GRANTED.map(([what, instead]) => (
            <li key={what}>
              <b>{what}</b>
              <span>{instead}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="mk-section">
        <h2 className="mk-h2">Review</h2>
        <Banner tone="info" title="Not published yet">
          Whether publishing is self-serve or reviewed by a person is not decided.
        </Banner>
      </section>

      <section className="mk-section">
        <h2 className="mk-h2">Getting paid</h2>
        <p className="mk-body">
          There is no way to be paid for an activity today. There is no per-activity purchase, no
          revenue share and no payout anywhere in the licensing model.
        </p>
      </section>

      <section className="mk-section">
        <h2 className="mk-h2">First party uses this same path</h2>
        <p className="mk-body">
          Every activity in the catalog is published through this contract, and none of them holds a
          capability yours cannot declare.
        </p>
        <div className="mk-cta">
          <Link to="/sign-in">
            <Button>Sign in</Button>
          </Link>
          <Link to="/activities">
            <Button variant="ghost">Browse activities</Button>
          </Link>
        </div>
        <p className="mk-hint">Publisher applications are not open yet.</p>
      </section>
    </MarketingShell>
  );
}
